/**
 * WhatsAppAnalyticsPage
 *
 * Performance dashboard for WhatsApp campaigns. Shows aggregate KPIs at
 * the top, an engagement-over-time line chart, and a table of recent
 * campaigns with delivery + read + reply rates.
 *
 * Data is mocked today — wire to `GET /api/v1/whatsapp/analytics` once
 * the backend persists campaigns.
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Send,
  CheckCheck,
  Eye,
  Reply,
  DollarSign,
  TrendingUp,
  TrendingDown,
  ArrowLeft,
  MessageCircle,
  Loader2,
} from "lucide-react";
import { whatsappApi, whatsappAccountsApi } from "../api";
import type { WhatsAppCampaign } from "../types";
import WhatsAppSubNav from "../components/WhatsAppSubNav";

const MOCK_BRAND_ID = "brand-demo";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { Button } from "../../../shared/components/ui/Button";
import { QualityRating } from "../components/QualityRating";

interface CampaignRow {
  id: string;
  name: string;
  templateName: string;
  sentAt: string;
  recipients: number;
  delivered: number;
  read: number;
  replied: number;
  costUSD: number;
}

const MOCK_CAMPAIGNS: CampaignRow[] = [
  {
    id: "c1",
    name: "Black Friday — VIP",
    templateName: "promo_descuento",
    sentAt: "2026-05-15T11:00:00Z",
    recipients: 340,
    delivered: 332,
    read: 298,
    replied: 47,
    costUSD: 19.48,
  },
  {
    id: "c2",
    name: "Carritos abandonados sábado",
    templateName: "carrito_abandonado",
    sentAt: "2026-05-13T19:30:00Z",
    recipients: 175,
    delivered: 168,
    read: 142,
    replied: 28,
    costUSD: 10.03,
  },
  {
    id: "c3",
    name: "Reactivación 30d",
    templateName: "reactivacion_inactivos",
    sentAt: "2026-05-11T10:00:00Z",
    recipients: 890,
    delivered: 854,
    read: 612,
    replied: 73,
    costUSD: 50.99,
  },
  {
    id: "c4",
    name: "Confirmaciones diarias",
    templateName: "pedido_confirmado",
    sentAt: "2026-05-10T12:00:00Z",
    recipients: 2100,
    delivered: 2089,
    read: 1900,
    replied: 11,
    costUSD: 20.58,
  },
  {
    id: "c5",
    name: "Cumpleaños mayo",
    templateName: "feliz_cumple",
    sentAt: "2026-05-08T09:00:00Z",
    recipients: 95,
    delivered: 93,
    read: 87,
    replied: 22,
    costUSD: 5.44,
  },
];

const TIME_SERIES = [
  { day: "Lun", entregado: 96, leido: 78, respondido: 14 },
  { day: "Mar", entregado: 98, leido: 81, respondido: 16 },
  { day: "Mié", entregado: 95, leido: 79, respondido: 13 },
  { day: "Jue", entregado: 97, leido: 84, respondido: 17 },
  { day: "Vie", entregado: 99, leido: 88, respondido: 21 },
  { day: "Sáb", entregado: 96, leido: 82, respondido: 19 },
  { day: "Dom", entregado: 92, leido: 75, respondido: 12 },
];

export function WhatsAppAnalyticsPage() {
  /* ─── Fetch del backend real con fallback a mocks ─── */
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [timeSeries, setTimeSeries] = useState(TIME_SERIES);
  const [usingMock, setUsingMock] = useState(false);

  /* Accounts count para el subnav */
  const [accountsCount, setAccountsCount] = useState<number | undefined>(undefined);
  useEffect(() => {
    whatsappAccountsApi.list().then((r) => {
      if (r.success) setAccountsCount(r.data.length);
    });
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = await whatsappApi.analytics(MOCK_BRAND_ID, 30);
      if (res.success && res.data?.totals?.campaignsCount > 0) {
        // Backend devolvió data real
        const recent: CampaignRow[] = (res.data.recentCampaigns || []).map(
          (c: WhatsAppCampaign) => ({
            id: c.id,
            name: c.segmentName || c.templateName || "Campaña",
            templateName: c.templateName,
            sentAt: c.sentAt || c.createdAt,
            recipients: c.recipientCount || 0,
            delivered: c.metrics?.delivered || 0,
            read: c.metrics?.read || 0,
            replied: c.metrics?.replied || 0,
            costUSD: c.estimatedCostUSD || 0,
          }),
        );
        setCampaigns(recent);
        setTimeSeries(res.data.perDay);
        setUsingMock(false);
      } else {
        // Sin data real → mostramos los mocks para que la pantalla
        // siga siendo presentable durante demos.
        setCampaigns(MOCK_CAMPAIGNS);
        setTimeSeries(TIME_SERIES);
        setUsingMock(true);
      }
      setLoading(false);
    })();
  }, []);

  const totals = useMemo(() => {
    const sent = campaigns.reduce((a, c) => a + c.recipients, 0);
    const delivered = campaigns.reduce((a, c) => a + c.delivered, 0);
    const read = campaigns.reduce((a, c) => a + c.read, 0);
    const replied = campaigns.reduce((a, c) => a + c.replied, 0);
    const cost = campaigns.reduce((a, c) => a + c.costUSD, 0);
    return {
      sent,
      delivered,
      read,
      replied,
      cost,
      deliveryRate: sent ? (delivered / sent) * 100 : 0,
      readRate: delivered ? (read / delivered) * 100 : 0,
      replyRate: read ? (replied / read) * 100 : 0,
    };
  }, [campaigns]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-500">
          <Loader2 size={20} className="animate-spin" />
          <span>Cargando métricas…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 md:p-8 animate-fade-in">
      <div className="mx-auto max-w-7xl space-y-6">
        <WhatsAppSubNav active="analytics" accountsCount={accountsCount} />

        {usingMock && (
          <div className="px-4 py-2.5 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-700">
            ℹ Mostrando datos de demo porque todavía no enviaste campañas reales.
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-4xl font-bold text-slate-900 tracking-tight">
                Métricas de WhatsApp
              </h1>
              <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold">
                Últimos 30 días
              </span>
            </div>
            <p className="text-slate-500 text-lg">
              Performance de tus campañas: entrega, lectura, respuesta y costo.
            </p>
          </div>
          <Link to="/whatsapp">
            <Button variant="primary">
              <MessageCircle size={16} />
              Nueva campaña
            </Button>
          </Link>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <KPICard
            label="Mensajes enviados"
            value={totals.sent.toLocaleString()}
            icon={Send}
          />
          <KPICard
            label="Tasa de entrega"
            value={`${totals.deliveryRate.toFixed(1)}%`}
            icon={CheckCheck}
            highlight="emerald"
          />
          <KPICard
            label="Tasa de lectura"
            value={`${totals.readRate.toFixed(1)}%`}
            icon={Eye}
            highlight="blue"
          />
          <KPICard
            label="Tasa de respuesta"
            value={`${totals.replyRate.toFixed(1)}%`}
            icon={Reply}
            highlight="violet"
          />
          <KPICard
            label="Costo total (USD)"
            value={`$${totals.cost.toFixed(2)}`}
            icon={DollarSign}
            highlight="slate"
          />
        </div>

        {/* Chart + Quality */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="font-semibold text-slate-900">
                  Engagement por día
                </h3>
                <p className="text-xs text-slate-500">
                  % entregado / leído / respondido en los últimos 7 días
                </p>
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={timeSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="day" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 12,
                      border: "1px solid #e2e8f0",
                      fontSize: 12,
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="entregado"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="leido"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="respondido"
                    stroke="#8b5cf6"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Quality rating side card */}
          <div>
            <QualityRating
              tier="green"
              score={92}
              dailyLimit={10000}
              usedToday={3540}
            />
          </div>
        </div>

        {/* Recent campaigns table */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-900">
                Campañas recientes
              </h3>
              <p className="text-xs text-slate-500">
                {campaigns.length} {campaigns.length === 1 ? "campaña" : "campañas"} en los últimos 30 días{usingMock ? " · datos de demo" : ""}
              </p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-6 py-3 font-medium">Campaña</th>
                  <th className="text-left px-4 py-3 font-medium">Template</th>
                  <th className="text-right px-4 py-3 font-medium">Enviados</th>
                  <th className="text-right px-4 py-3 font-medium">Entrega</th>
                  <th className="text-right px-4 py-3 font-medium">Lectura</th>
                  <th className="text-right px-4 py-3 font-medium">Respuesta</th>
                  <th className="text-right px-4 py-3 font-medium">Costo</th>
                  <th className="text-right px-6 py-3 font-medium">Fecha</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {campaigns.map((c) => {
                  const dRate = (c.delivered / c.recipients) * 100;
                  const rRate = (c.read / c.delivered) * 100;
                  const rpRate = (c.replied / c.read) * 100;
                  return (
                    <tr
                      key={c.id}
                      className="hover:bg-slate-50/60 transition-colors"
                    >
                      <td className="px-6 py-3.5">
                        <p className="font-medium text-slate-900">{c.name}</p>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="font-mono text-xs text-slate-500">
                          {c.templateName}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-right font-semibold text-slate-900">
                        {c.recipients.toLocaleString()}
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        <span className="font-medium text-emerald-600">
                          {dRate.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        <span className="font-medium text-blue-600">
                          {rRate.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        <span className="font-medium text-violet-600">
                          {rpRate.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-right font-mono text-slate-700">
                        ${c.costUSD.toFixed(2)}
                      </td>
                      <td className="px-6 py-3.5 text-right text-xs text-slate-500">
                        {new Date(c.sentAt).toLocaleDateString("es-AR", {
                          day: "numeric",
                          month: "short",
                        })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── KPI Card ─── */
interface KPICardProps {
  label: string;
  value: string;
  change?: string;
  up?: boolean;
  icon: typeof Send;
  highlight?: "emerald" | "blue" | "violet" | "slate";
}

function KPICard({
  label,
  value,
  change,
  up,
  icon: Icon,
  highlight = "slate",
}: KPICardProps) {
  const accent: Record<NonNullable<KPICardProps["highlight"]>, string> = {
    emerald: "bg-emerald-50 text-emerald-600",
    blue: "bg-blue-50 text-blue-600",
    violet: "bg-violet-50 text-violet-600",
    slate: "bg-slate-50 text-slate-600",
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <div
          className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent[highlight]}`}
        >
          <Icon size={16} />
        </div>
        {change && (
          <span
            className={`text-xs font-medium flex items-center gap-0.5 ${
              up ? "text-emerald-600" : "text-red-500"
            }`}
          >
            {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {change}
          </span>
        )}
      </div>
      <p className="text-2xl font-bold text-slate-900 tracking-tight">
        {value}
      </p>
      <p className="text-xs text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}

export default WhatsAppAnalyticsPage;
